import type { AllpayUserDTO } from '@/api/trafikportalen';
import { canAccessDepartmentResource } from '@/helpers/departmentPermissions';
import type { BulkAllpayTraveller } from '@/helpers/bulkAllpayAssign';

export type BulkAllpayRemoveSkipReason =
  | 'no-allpay'
  | 'no-access'
  | 'ambiguous-department';

export type BulkAllpayRemovePlan =
  | { action: 'remove'; users: AllpayUserDTO[] }
  | { action: 'skip'; reason: BulkAllpayRemoveSkipReason };

const getMatchingAllpayUsers = (
  traveller: BulkAllpayTraveller,
  filteredDepartmentIds: string[],
): AllpayUserDTO[] => {
  if (filteredDepartmentIds.length === 0) {
    return traveller.allpayUsers;
  }

  return traveller.allpayUsers.filter((user) =>
    filteredDepartmentIds.includes(user.department_id),
  );
};

export const getBulkAllpayRemovePlan = (
  traveller: BulkAllpayTraveller,
  filteredDepartmentIds: string[] = [],
): BulkAllpayRemovePlan => {
  const matchingUsers = getMatchingAllpayUsers(
    traveller,
    filteredDepartmentIds,
  );

  if (matchingUsers.length === 0) {
    return { action: 'skip', reason: 'no-allpay' };
  }

  const matchingDepartmentIds = [
    ...new Set(matchingUsers.map((user) => user.department_id)),
  ];

  if (matchingDepartmentIds.length > 1) {
    return { action: 'skip', reason: 'ambiguous-department' };
  }

  if (!canAccessDepartmentResource(matchingDepartmentIds[0], true)) {
    return { action: 'skip', reason: 'no-access' };
  }

  return { action: 'remove', users: matchingUsers };
};

export const getBulkAllpayCreditReleased = (
  plans: BulkAllpayRemovePlan[],
): number => {
  return plans.reduce((total, plan) => {
    if (plan.action !== 'remove') {
      return total;
    }

    return total + plan.users.reduce((sum, user) => sum + user.credit, 0);
  }, 0);
};
